import React from 'react';
import { cn } from '@/lib/utils';
import { Building2, Users, Network, Store } from 'lucide-react';
import type { ClientType } from '@/types/client';
import { CLIENT_TYPE_LABELS } from '@/types/client';

interface ClientTypeBadgeProps {
  type: ClientType;
  showIcon?: boolean;
  variant?: 'default' | 'compact';
  className?: string;
}

const BUYER_TYPE_CONFIG: Record<ClientType, {
  label: string;
  icon: typeof Store;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  pharmacie: {
    label: CLIENT_TYPE_LABELS.pharmacie,
    icon: Store,
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-700 dark:text-green-300',
    borderColor: 'border-green-200 dark:border-green-800',
  },
  parapharmacie: {
    label: CLIENT_TYPE_LABELS.parapharmacie,
    icon: Store,
    bgColor: 'bg-teal-100 dark:bg-teal-900/30',
    textColor: 'text-teal-700 dark:text-teal-300',
    borderColor: 'border-teal-200 dark:border-teal-800',
  },
  centrale: {
    label: CLIENT_TYPE_LABELS.centrale,
    icon: Network,
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    textColor: 'text-orange-700 dark:text-orange-300',
    borderColor: 'border-orange-200 dark:border-orange-800',
  },
  magasin_bio: {
    label: CLIENT_TYPE_LABELS.magasin_bio,
    icon: Store,
    bgColor: 'bg-lime-100 dark:bg-lime-900/30',
    textColor: 'text-lime-700 dark:text-lime-300',
    borderColor: 'border-lime-200 dark:border-lime-800',
  },
  therapeute: {
    label: CLIENT_TYPE_LABELS.therapeute,
    icon: Store,
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    textColor: 'text-purple-700 dark:text-purple-300',
    borderColor: 'border-purple-200 dark:border-purple-800',
  },
  member: {
    label: CLIENT_TYPE_LABELS.member,
    icon: Users,
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    textColor: 'text-cyan-700 dark:text-cyan-300',
    borderColor: 'border-cyan-200 dark:border-cyan-800',
  },
  // Backward compatibility
  independent: {
    label: CLIENT_TYPE_LABELS.independent,
    icon: Building2,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-700 dark:text-blue-300',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  central: {
    label: CLIENT_TYPE_LABELS.central,
    icon: Network,
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    textColor: 'text-orange-700 dark:text-orange-300',
    borderColor: 'border-orange-200 dark:border-orange-800',
  },
};

// Default fallback config for unknown types
const DEFAULT_CONFIG = {
  label: 'Inconnu',
  icon: Store,
  bgColor: 'bg-gray-100 dark:bg-gray-900/30',
  textColor: 'text-gray-700 dark:text-gray-300',
  borderColor: 'border-gray-200 dark:border-gray-800',
};

export const ClientTypeBadge: React.FC<ClientTypeBadgeProps> = ({
  type,
  showIcon = true,
  variant = 'default',
  className = '',
}) => {
  const config = BUYER_TYPE_CONFIG[type] || DEFAULT_CONFIG;
  const Icon = config.icon;

  const baseClasses = cn(
    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-200',
    config.bgColor,
    config.textColor,
    'hover:scale-105 hover:shadow-sm',
    className
  );

  const compactClasses = cn(
    'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
    config.bgColor,
    config.textColor,
    className
  );

  const classes = variant === 'compact' ? compactClasses : baseClasses;

  return (
    <span className={classes}>
      {showIcon && <Icon className="h-3 w-3 flex-shrink-0" />}
      <span className="font-medium">{config.label}</span>
    </span>
  );
};

export default ClientTypeBadge;
